import { useEffect, useState } from "react";
import { useRevalidateOnFocus } from "@/hooks/useRevalidateOnFocus";
import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Building2, Mail, Clock, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useRoleLabel } from "@/hooks/useRoleLabel";
import { track } from "@/lib/posthog";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

/** The fields of a received invitation the row below renders. */
interface ReceivedInvite {
  id: number;
  invite_token: string;
  invitee_email: string;
  invited_role: string;
  expires_at: string;
  org_id: string;
  organisation_name: string | null;
}

/** An org whose name was never recorded still has to be nameable on screen. */
function orgDisplayName(invite: ReceivedInvite): string {
  return invite.organisation_name || `Organisation ${invite.org_id}`;
}

/** Which org invited you, to what role, and how long the link stands. */
function InviteSummary({ invite }: { invite: ReceivedInvite }) {
  const { t } = useLanguage();
  const roleLabel = useRoleLabel();

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Building2 className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">{orgDisplayName(invite)}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Mail className="h-3 w-3" />
            {invite.invitee_email}
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            {roleLabel(invite.invited_role)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {t.settings.invitations.expiresIn.replace(
              "{time}",
              format(new Date(invite.expires_at), "MMM d, yyyy")
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The screen behind an empty list.
 *
 * With nothing to show, the empty state and a failed load are the same screen —
 * and "no pending invitations" is a claim, not a shrug. Say which one it is
 * rather than letting a blip read as "nobody invited you".
 */
function NoInvitations({ loadError }: { loadError: string | null }) {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted">
        {loadError ? (
          <AlertCircle className="h-6 w-6 text-destructive" />
        ) : (
          <Mail className="h-6 w-6 text-muted-foreground" />
        )}
      </div>
      <p className="text-sm font-medium">
        {loadError ? t.settings.invitations.failedLoadInvites : t.common.noPendingInvitations}
      </p>
      <p className="max-w-sm text-sm text-muted-foreground">
        {loadError || t.settings.invitations.noPendingDesc}
      </p>
    </div>
  );
}

/**
 * One invitation addressed to the signed-in user, with its accept/decline pair.
 * Lives outside the list so the list body stays readable — the same shape the
 * inviter-side rows in `InvitationRows.tsx` already use.
 *
 * `observer` because `invite` is an MST node: the list refetch replaces the
 * whole array today, but a future in-place edit would not reach this row
 * without it.
 */
const ReceivedInviteRow = observer(function ReceivedInviteRow({
  invite,
  isAccepting,
  isDeclining,
  onAccept,
  onDecline,
}: {
  invite: ReceivedInvite;
  isAccepting: boolean;
  isDeclining: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const { t } = useLanguage();
  // Either action in flight disables both — the pair acts on one invitation.
  const busy = isAccepting || isDeclining;

  return (
    <div className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <InviteSummary invite={invite} />
      <div className="flex shrink-0 items-center gap-2">
        <Button variant="outline" size="sm" onClick={onDecline} disabled={busy}>
          {isDeclining ? t.common.processing : t.settings.invitations.decline}
        </Button>
        <Button
          {...pageInfoTarget("invitations.accept")}
          size="sm"
          onClick={onAccept}
          disabled={busy}
        >
          {isAccepting ? t.common.processing : t.settings.invitations.accept}
        </Button>
      </div>
    </div>
  );
});

/**
 * Everything the list DOES: keeping the invitations read, and answering one.
 *
 * Split from the component below so the render is only the three screens it
 * can be in — a list, an empty state, a spinner — and every rule about when a
 * refetch runs or which toast a failure raises lives in one place.
 */
function useReceivedInvitations() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { userProfile, userId } = useAuth();
  const organizationStore = useOrganizationStore();
  const authStore = useAuthStore();
  const { refetch: refetchOrganizations } = useOrganizationContext();
  const [acceptingInvite, setAcceptingInvite] = useState<string | null>(null);
  const [decliningInvite, setDecliningInvite] = useState<string | null>(null);
  const [inviteToDecline, setInviteToDecline] = useState<{ token: string; orgName: string } | null>(
    null
  );

  // Fetch invitations when component mounts or user email changes
  useEffect(() => {
    if (userProfile?.email) {
      organizationStore.fetchUserInvitations(userProfile.email);
    }
  }, [userProfile?.email, organizationStore]);

  // `organization_invites` is not in the realtime publication, so there is no
  // event to subscribe to — being invited while sitting on this page showed
  // nothing until a reload. (The inviter-side list in InvitationsSection has a
  // subscription to that table, but it is dead for the same reason.)
  //
  // `background: true`: the refresh is not something the user asked for, so it
  // must not put a spinner over a list that is already correct, and a failure
  // must leave the list standing (see fetchUserInvitations).
  useRevalidateOnFocus(
    () => {
      if (userProfile?.email)
        organizationStore.fetchUserInvitations(userProfile.email, { background: true });
    },
    { enabled: Boolean(userProfile?.email) }
  );

  async function handleAcceptInvite(inviteToken: string, role: string) {
    if (!userId) {
      toast({
        variant: "destructive",
        title: t.common.error,
        description: t.settings.personal.notAuthenticated,
      });
      return;
    }

    setAcceptingInvite(inviteToken);

    try {
      const result = await organizationStore.acceptInvitation(inviteToken, userId);
      // Reached from in-app settings, so the account always existed.
      if (result.success) track("invite_accepted", { role, had_account: true });

      if (result.success) {
        toast({
          title: t.common.success,
          description: t.settings.invitations.acceptedSuccess,
        });

        // Reload auth session to get updated JWT claims
        await authStore.loadSession();

        // Refresh organization list
        await refetchOrganizations();

        // Refresh invitations list
        if (userProfile.email) {
          await organizationStore.fetchUserInvitations(userProfile.email);
        }
      } else {
        throw new Error(result.error || "Failed to accept invitation");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t.common.error,
        description: error instanceof Error ? error.message : t.settings.invitations.acceptFailed,
      });
    } finally {
      setAcceptingInvite(null);
    }
  }

  async function handleDeclineConfirm() {
    const invite = inviteToDecline;
    if (!invite) return;

    setDecliningInvite(invite.token);
    setInviteToDecline(null);

    try {
      const result = await organizationStore.declineInvitation(invite.token);

      if (!result.success) {
        throw new Error(result.error || "Failed to decline invitation");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t.common.error,
        description: error instanceof Error ? error.message : t.settings.invitations.declineFailed,
      });
      return;
    } finally {
      setDecliningInvite(null);
    }

    toast({
      title: t.common.success,
      description: t.settings.invitations.declinedSuccess,
    });

    // Refetch drops the declined invite — the list only shows pending ones.
    // Outside the try so a refetch failure can't masquerade as a failed decline.
    if (userProfile?.email) {
      await organizationStore.fetchUserInvitations(userProfile.email);
    }
  }

  return {
    acceptingInvite,
    decliningInvite,
    inviteToDecline,
    setInviteToDecline,
    handleAcceptInvite,
    handleDeclineConfirm,
  };
}

export const UserInvitationsList = observer(() => {
  const { t } = useLanguage();
  const organizationStore = useOrganizationStore();
  const {
    acceptingInvite,
    decliningInvite,
    inviteToDecline,
    setInviteToDecline,
    handleAcceptInvite,
    handleDeclineConfirm,
  } = useReceivedInvitations();

  const invitations = organizationStore.invitations;
  const loadError = organizationStore.invitationsError;

  // The page title already says "Invitations", so the list stands on its own
  // here without a SettingsSection label repeating it.
  if (organizationStore.isLoadingInvitations) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!invitations || invitations.length === 0) {
    return <NoInvitations loadError={loadError} />;
  }

  return (
    <>
      {/* A refresh that failed over a list we can still show: say so, but keep
          showing it. */}
      {loadError && (
        <p className="pb-3 text-sm text-muted-foreground">
          {t.settings.invitations.failedLoadInvites}
        </p>
      )}
      <div className="divide-y divide-border">
        {invitations.map((invite) => (
          <ReceivedInviteRow
            key={invite.id}
            invite={invite}
            isAccepting={acceptingInvite === invite.invite_token}
            isDeclining={decliningInvite === invite.invite_token}
            onAccept={() => handleAcceptInvite(invite.invite_token, invite.invited_role)}
            onDecline={() =>
              setInviteToDecline({
                token: invite.invite_token,
                orgName: orgDisplayName(invite),
              })
            }
          />
        ))}
      </div>

      <ConfirmDialog
        open={inviteToDecline !== null}
        onOpenChange={(open) => !open && setInviteToDecline(null)}
        onConfirm={handleDeclineConfirm}
        title={t.settings.invitations.declineInviteTitle}
        description={t.settings.invitations.declineInviteDescription.replace(
          "{org}",
          inviteToDecline?.orgName ?? ""
        )}
        confirmText={t.settings.invitations.decline}
        cancelText={t.common.cancel}
      />
    </>
  );
});
